import { PlatformCatalogAccessMatchingRuleCriteria } from "@bvhi/backend-api";
import { Button, Card, CardContent, CardHeader, FormTextField, IconButton, Stack, Tooltip } from "@bvhi/ui-base";
import { AddIcon, DeleteIcon } from "@bvhi/ui-icons";
import { useCallback, useState } from "react";
import { useFieldArray, UseFieldArrayRemove, useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { ConfirmDialog } from "../../../../../dialogs/confirm-dialog";
import { PlatformCatalogAccessMatchingRuleColumnSelectField } from "../components/catalog-rules/platform-catalog-access-matching-rule-column-select-field";
import { PlatformCatalogAccessMatchingRuleCriteriaSelectField } from "../components/catalog-rules/platform-catalog-access-matching-rule-criteria-select-field";

interface Props {
  index: number;
  removeMatchingBlock: UseFieldArrayRemove;
  options: string[];
}

export const CatalogsUpdateMatchingRulesBlockCard = ({ index, options, removeMatchingBlock }: Props) => {
  const { control, trigger } = useFormContext();
  const { t } = useTranslation(["catalogs", "dialogs", "common"]);
  const [rulesetIndexToDelete, setRulesetIndexToDelete] = useState<number>();
  const [ruleIndexToDelete, setRuleIndexToDelete] = useState<number>();

  const { fields, remove, append } = useFieldArray({
    control,
    name: `platformCatalogAccessMatchingBlocks.${index}.platformCatalogAccessMatchingRules`,
  });

  const onDeleteRulesetClose = useCallback(
    (confirmed: boolean) => {
      if (confirmed) removeMatchingBlock(rulesetIndexToDelete);
      setRulesetIndexToDelete(undefined);
    },
    [removeMatchingBlock, rulesetIndexToDelete]
  );

  const onDeleteRuleClose = useCallback(
    (confirmed: boolean) => {
      if (confirmed) remove(ruleIndexToDelete);
      setRuleIndexToDelete(undefined);
    },
    [remove, ruleIndexToDelete]
  );

  return (
    <>
      <Card elevation={0}>
        <CardHeader title={t("catalogs:catalogs.cards.matching.rules.block.title", { number: index + 1 })} />
        <CardContent restrictContentWidth={false}>
          <Stack direction="column" spacing={2}>
            {fields.map((field, nestedIndex) => {
              return (
                <Stack key={field.id} alignItems="top" direction="row" spacing={2}>
                  <PlatformCatalogAccessMatchingRuleColumnSelectField
                    name={`platformCatalogAccessMatchingBlocks.${index}.platformCatalogAccessMatchingRules.${nestedIndex}.column`}
                    options={options}
                  />
                  <PlatformCatalogAccessMatchingRuleCriteriaSelectField
                    name={`platformCatalogAccessMatchingBlocks.${index}.platformCatalogAccessMatchingRules.${nestedIndex}.criteria`}
                    changeCallback={() => {
                      void trigger(`platformCatalogAccessMatchingBlocks.${index}.platformCatalogAccessMatchingRules.${nestedIndex}.value`);
                    }}
                  />
                  <FormTextField
                    fullWidth
                    label={t("catalogs:catalogs.form.fields.value.label")}
                    name={`platformCatalogAccessMatchingBlocks.${index}.platformCatalogAccessMatchingRules.${nestedIndex}.value`}
                    placeholder={t("catalogs:catalogs.form.fields.value.placeholder")}
                  />
                  <Tooltip title={t("common:button.tooltip.delete.enabled")}>
                    <IconButton color="error" onClick={() => setRuleIndexToDelete(nestedIndex)} sx={{ width: 48, height: 48 }}>
                      <DeleteIcon />
                    </IconButton>
                  </Tooltip>
                </Stack>
              );
            })}
            <Stack direction="row-reverse" justifyContent="space-between" spacing={2}>
              <Button
                onClick={() => append({ column: "", value: "", criteria: PlatformCatalogAccessMatchingRuleCriteria.EQUALS })}
                size="xs"
                startIcon={<AddIcon />}
              >
                {t("catalogs:catalogs.actions.add.rule")}
              </Button>
              <Button color="error" onClick={() => setRulesetIndexToDelete(index)} size="xs" startIcon={<DeleteIcon />}>
                {t("catalogs:catalogs.actions.remove.ruleset")}
              </Button>
            </Stack>
          </Stack>
        </CardContent>
      </Card>
      <ConfirmDialog
        onClose={onDeleteRulesetClose}
        open={rulesetIndexToDelete !== undefined}
        overwrite={{
          title: t("dialogs:dialogs.delete.rule.set.title"),
          confirmButtonColor: "error",
          confirmButtonIcon: <DeleteIcon />,
        }}
      />
      <ConfirmDialog
        onClose={onDeleteRuleClose}
        open={ruleIndexToDelete !== undefined}
        overwrite={{
          title: t("dialogs:dialogs.delete.rule.title"),
          confirmButtonColor: "error",
          confirmButtonIcon: <DeleteIcon />,
        }}
      />
    </>
  );
};
